import { createApp } from 'vue'
import { createPinia } from 'pinia'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import './style.css'
import App from './App.vue'
import ResultDetailApp from './replay/ResultDetailApp.vue'
import { i18n } from './i18n'
import { initImageProtection, initViewportVars } from './composables'
import { APP_ENV, initRuntimeConfig } from './runtimeConfig'

// dayjs UTC plugin — 全域註冊，讓任何元件都能用 dayjs.utc() / .utc() / 在 UTC
// 視角下 format。本專案歷史頁顯示時區走 offsetMs 偽 UTC 方案，需要這個 plugin。
dayjs.extend(utc)

/**
 * 開機序列。
 *
 * 🛑 第一件事一定是 `await initRuntimeConfig()` —— runtimeConfig 的 API_BASE /
 *    IMAGES_BASE / GAME_H5_BASE 等是 live binding（`export let`），mount 之前套好，
 *    之後所有 lazy chunk（per-game content.ts / assets.ts）與 API 呼叫才讀得到值。
 *    fetch 由 index.html 於 <head> 就發出（與 bundle 下載並行），這裡通常只是等它落地。
 */
/**
 * 動態 chunk 載入失敗（Vite 的 `vite:preloadError`）。
 *
 * 實務上只會發生在開機階段 —— per-game content / historyBoard / 自訂 modal 與四個
 * 共用 modal 都在 `ensureGameLoaded()` 一次載完，之後執行期沒有其他 dynamic import。
 * 兩種成因：弱網掉了一次 request，或玩家手上的 index.html 指向已被換掉的 chunk hash。
 * 兩者「重新拿一次 index.html」都能解決（index.html 是 no-store）→ 自動重整一次。
 *
 * 🛑 `PRELOAD_RELOADED_KEY` 這道閘不要拿掉：chunk 若是真的永久不存在，沒有閘就會
 *    變成無限重整迴圈。第二次以後不重整，讓錯誤往下走到 registry 的 catch →
 *    pageError → PageErrorScreen，至少玩家看得到訊息。
 */
const PRELOAD_RELOADED_KEY = 'aurum:preload-reloaded'
window.addEventListener('vite:preloadError', (event: Event) => {
  event.preventDefault() // 不讓 Vite 把它拋成 uncaught error
  let alreadyReloaded = true
  try {
    alreadyReloaded = sessionStorage.getItem(PRELOAD_RELOADED_KEY) === '1'
    sessionStorage.setItem(PRELOAD_RELOADED_KEY, '1')
  } catch {
    // sessionStorage 不可用（隱私模式等）→ 當成「已重整過」，不冒無限迴圈的風險
  }
  console.warn(
    '[web][boot] chunk preload 失敗',
    alreadyReloaded ? '（已重整過一次，不再重試）' : '（自動重整一次）',
  )
  if (!alreadyReloaded) window.location.reload()
})

const boot = async (): Promise<void> => {
  await initRuntimeConfig()

  console.log(
    `%c[web][Build] ${APP_ENV || 'unknown'} - ${__BUILD_TIME__}`,
    'background: #ffffff; color: #000000; padding: 2px 6px; font-weight: bold;',
  )

  // 防圖片下載 + viewport CSS vars 同步 — 詳見各 composable 內 doc
  initImageProtection()
  initViewportVars()

  // 開機分流（單 entry、非路由）：
  //   query 帶 record_id + token → 對接商盤面回放模式,掛 ResultDetailApp
  //   （不碰登入 / Cocos iframe / postMessage,只渲染單局 HistoryDetail）
  //   否則 → 正常遊戲容器 App.vue
  const q = new URLSearchParams(window.location.search)
  const isReplay = q.has('record_id') && q.has('token')
  const RootApp = isReplay ? ResultDetailApp : App

  createApp(RootApp).use(createPinia()).use(i18n).mount('#app')
}

void boot()
